import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import {
  Brain,
  BookOpen,
  TrendingUp,
  Flame,
  Clock,
  CheckCircle,
  AlertCircle,
  History
} from 'lucide-react';

const Dashboard = () => {
  const { user, updateProfile } = useAuth();
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dailyGoal, setDailyGoal] = useState(user?.daily_review_limit || 50);
  const [savingGoal, setSavingGoal] = useState(false);
  const [animalId, setAnimalId] = useState('panda');
  const [studyDictionary, setStudyDictionary] = useState(
    () => localStorage.getItem('dictionaryType') || 'hsk'
  );

  const cheerAnimals = [
    { id: 'panda', emoji: '🐼', name: 'Panda', message: 'Panda says: one more word!' },
    { id: 'cat', emoji: '🐱', name: 'Cat', message: 'Tiny steps, big progress!' },
    { id: 'rabbit', emoji: '🐰', name: 'Rabbit', message: 'Hop into your next review!' },
    { id: 'capybara', emoji: '🦫', name: 'Capybara', message: 'Stay calm and keep learning.' }
  ];
  const selectedAnimal = cheerAnimals.find((animal) => animal.id === animalId) || cheerAnimals[0];

  useEffect(() => {
    if (user?.daily_review_limit) {
      setDailyGoal(user.daily_review_limit);
    }
  }, [user?.daily_review_limit]);

  useEffect(() => {
    fetchStats(studyDictionary);
  }, [studyDictionary]);

  const fetchStats = async (dictionary = studyDictionary) => {
    try {
      const [statsRes, dailyRes] = await Promise.all([
        api.get('/api/stats/learning', { params: { dictionary_type: dictionary } }),
        api.get('/api/stats/daily', { params: { dictionary_type: dictionary } })
      ]);
      setStats(statsRes.data);
      setDailyStats(dailyRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoalSubmit = async (event) => {
    event.preventDefault();
    const goal = Number(dailyGoal);
    if (!Number.isInteger(goal) || goal < 1 || goal > 2000) {
      toast.error('Choose a goal between 1 and 2,000 cards');
      return;
    }

    setSavingGoal(true);
    try {
      await updateProfile({ daily_review_limit: goal });
      localStorage.setItem('dailyReviewLimit', String(goal));
      setDailyStats((current) => current ? {
        ...current,
        daily_goal: goal,
        progress_percent: Math.min(100, ((current.reviewed_today || 0) / goal) * 100)
      } : current);
      toast.success('Daily goal updated');
    } catch (error) {
      toast.error('Failed to update daily goal');
    } finally {
      setSavingGoal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] max-w-7xl mx-auto overflow-y-auto overflow-x-hidden px-4 sm:px-6 lg:px-8 py-8">
      {/* Study Buddy */}
      <div className="mb-8 overflow-hidden rounded-xl bg-gradient-to-br from-amber-50 via-white to-rose-50 p-4 shadow-md sm:p-5">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[170px_minmax(0,1fr)_minmax(220px,0.8fr)]">
          <div className="rounded-xl border border-amber-200 bg-white/75 p-4 shadow-sm">
            <span className="block text-sm font-semibold text-gray-700">Today's topic</span>
            <div className="mt-3 inline-flex w-full flex-col rounded-lg border border-amber-200 bg-white p-1" role="group" aria-label="Choose today's topic">
            {[
              { value: 'hsk', label: 'HSK' },
              { value: 'ielts', label: 'IELTS' }
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setStudyDictionary(option.value);
                  localStorage.setItem('dictionaryType', option.value);
                  localStorage.removeItem('studyTopic');
                }}
                className={`rounded-lg px-5 py-2 text-left text-sm font-semibold transition-colors ${
                  studyDictionary === option.value
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'text-gray-500 hover:bg-amber-50 hover:text-amber-700'
                }`}
                aria-pressed={studyDictionary === option.value}
              >
                {option.label}
              </button>
            ))}
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-xl border border-amber-200 bg-white/75 p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="animal-cheer-stage shrink-0" aria-label={`${selectedAnimal.name} cheering`}>
                <span className="animal-cheer-shadow" />
                <span className="animal-cheer-emoji" role="img" aria-hidden="true">{selectedAnimal.emoji}</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold uppercase tracking-wide text-amber-600">Study buddy</p>
                <h1 className="mt-1 text-xl font-bold text-gray-900">{selectedAnimal.message}</h1>
                <p className="mt-1 text-sm text-gray-600">Pick a little friend to cheer you on.</p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2" role="group" aria-label="Choose study buddy">
              {cheerAnimals.map((animal) => (
                <button
                  key={animal.id}
                  type="button"
                  onClick={() => setAnimalId(animal.id)}
                  className={`animal-choice ${animal.id === selectedAnimal.id ? 'animal-choice-active' : ''}`}
                  aria-label={`Choose ${animal.name}`}
                  title={animal.name}
                >
                  <span role="img" aria-hidden="true">{animal.emoji}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-amber-200 bg-white/75 p-4 shadow-sm">
            <h2 className="text-lg font-bold text-gray-900">Today's Progress</h2>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-gray-700">
                <Flame className="h-5 w-5 text-orange-500" />
                Daily goal
              </span>
              <span className="font-semibold text-gray-900">
                {dailyStats?.reviewed_today || 0} / {dailyStats?.daily_goal || 50}
              </span>
            </div>
            <div className="mt-3 h-3 w-full rounded-full bg-gray-200">
              <div
                className="h-3 rounded-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-500"
                style={{ width: `${dailyStats?.progress_percent || 0}%` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-xs text-gray-600">
              <span>{dailyStats?.progress_percent || 0}% complete</span>
              <span>{dailyStats?.due_today || 0} due</span>
            </div>
            <form onSubmit={handleGoalSubmit} className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3">
              <label htmlFor="daily-goal" className="sr-only">Daily review goal</label>
              <input
                id="daily-goal"
                type="number"
                min="1"
                max="2000"
                value={dailyGoal}
                onChange={(event) => setDailyGoal(event.target.value)}
                className="w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                aria-label="Daily review goal"
              />
              <span className="text-xs text-gray-500">cards</span>
              <button
                type="submit"
                disabled={savingGoal}
                className="ml-auto rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-600 disabled:opacity-50"
              >
                {savingGoal ? 'Saving...' : 'Save'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4">
        <Link
          to="/study"
          state={{
            dictionaryType: studyDictionary,
            topic: studyDictionary === 'ielts' ? 'ielts' : '',
            dailyReviewLimit: dailyStats?.daily_goal || user?.daily_review_limit || 50
          }}
          className="card bg-gradient-to-br from-primary-500 to-primary-600 text-white hover:shadow-xl transition-all duration-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-100 font-medium">Start Studying</p>
              <p className="text-3xl font-bold mt-2">
                {dailyStats?.due_today || 0} due
              </p>
              <p className="text-primary-100 text-sm mt-1">
                {dailyStats?.new_available || 0} new cards available
              </p>
            </div>
            <Brain className="w-16 h-16 text-primary-200" />
          </div>
        </Link>

        <Link
          to="/review-history"
          state={{ dictionaryType: studyDictionary }}
          className="card bg-gradient-to-br from-violet-500 to-purple-600 text-white hover:shadow-xl transition-all duration-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 font-medium">Review History</p>
              <p className="text-3xl font-bold mt-2">Past Cards</p>
              <p className="text-purple-100 text-sm mt-1">
                Reset & manage progress
              </p>
            </div>
            <History className="w-16 h-16 text-purple-200" />
          </div>
        </Link>

        <Link
          to="/dictionary"
          state={{ dictionaryType: studyDictionary }}
          className="card bg-gradient-to-br from-cyan-500 to-teal-600 text-white hover:shadow-xl transition-all duration-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-cyan-100 font-medium">Dictionary</p>
                <p className="text-3xl font-bold mt-2">{studyDictionary === 'ielts' ? 'IELTS' : 'HSK Levels'}</p>
              <p className="text-cyan-100 text-sm mt-1">
                Browse by proficiency
              </p>
            </div>
            <BookOpen className="w-16 h-16 text-cyan-200" />
          </div>
        </Link>

        <Link
          to="/stats"
          state={{ dictionaryType: studyDictionary }}
          className="card bg-gradient-to-br from-green-500 to-green-600 text-white hover:shadow-xl transition-all duration-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 font-medium">View Progress</p>
              <p className="text-3xl font-bold mt-2">
                {stats?.streak_days || 0} days
              </p>
              <p className="text-green-100 text-sm mt-1">Current streak 🔥</p>
            </div>
            <TrendingUp className="w-16 h-16 text-green-200" />
          </div>
        </Link>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Cards</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.total_cards || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Learning</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.learning_cards || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Mastered</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.mastered_cards || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Retention</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats?.avg_retention || 0}%
              </p>
            </div>
            <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-accent-600" />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
